import React, { FC } from 'react'
import {
  Box,
  Modal,
  Typography,
  Divider,
  Switch,
  Stack,
  Card,
  Tooltip,
} from '@mui/material'
import { useSettingQuery, useUpdateSettingMutation } from '@/features/auth'
import { Setting } from '@/features/auth/types/setting'
import SettingsIcon from '@mui/icons-material/Settings'
import { useNavigate } from 'react-router-dom'
import { settingDescriptions } from '@/constants/settingDescriptions'
import { CustomButton } from '@/apps/legacy/components/molecules/CustomButton'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const SettingModalBody: FC<Props> = ({ isOpen, onClose }) => {
  const { data: customSetting } = useSettingQuery()
  const updateSettingMutation = useUpdateSettingMutation()
  const navigate = useNavigate()

  const updateSetting = (
    key: Parameters<typeof updateSettingMutation.mutate>[0]['key'],
    value: Setting[keyof Setting]
  ) => {
    updateSettingMutation.mutate({ key, value })
  }

  if (!customSetting) return <></>

  // 軽量モードが生成に効くか。server が model 設定表から導出して配る値をそのまま使う
  // (front で version から推測しない — 版と世代が食い違う状態があるため)。
  const quickModeEffective = customSetting.is_quick_mode_effective ?? true

  const handleNavigateToDetailSettings = () => {
    onClose()
    navigate('/portal/soap-generation')
  }

  return (
    <Modal open={isOpen} onClose={onClose} sx={{ zIndex: 1303 }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: '700px' },
          maxHeight: { xs: '90vh', sm: '80vh' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflowY: 'auto',
        }}
      >
        <Stack spacing={3} sx={{ px: 2, py: 3 }}>
          {/* モーダルタイトル */}
          <Typography
            variant="h5"
            sx={{
              mb: 2,
            }}
          >
            基本設定
          </Typography>

          {/* 音声認識設定セクション */}
          <Card
            sx={{
              p: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
              },
              transition: 'box-shadow 0.3s ease',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: colorTokens.blue[700],
                mb: 2,
                pb: 1.5,
                borderBottom: `2px solid ${colorTokens.blue[50]}`,
              }}
            >
              {settingDescriptions.speechRecognition.title}
            </Typography>

            <Stack spacing={3}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {
                      settingDescriptions.speechRecognition.items.is_quick_mode
                        .title
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      settingDescriptions.speechRecognition.items.is_quick_mode
                        .description
                    }
                  </Typography>
                </Box>
                {/* 効かない世代では軽量モデルが使われないため、オフ固定の変更不可で見せる。
                    disabled な Switch はホバーを拾わないため span でラップして Tooltip を出す。 */}
                {quickModeEffective ? (
                  <Switch
                    color="primary"
                    checked={customSetting.is_quick_mode ?? false}
                    onChange={() =>
                      updateSetting(
                        'is_quick_mode',
                        !customSetting.is_quick_mode
                      )
                    }
                  />
                ) : (
                  <Tooltip title="現在のSOAPモデルでは使用されません">
                    <span>
                      <Switch color="primary" checked={false} disabled />
                    </span>
                  </Tooltip>
                )}
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {
                      settingDescriptions.speechRecognition.items
                        .is_noise_cancelling.title
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      settingDescriptions.speechRecognition.items
                        .is_noise_cancelling.description
                    }
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={customSetting.is_noise_cancelling ?? false}
                  onChange={() =>
                    updateSetting(
                      'is_noise_cancelling',
                      !customSetting.is_noise_cancelling
                    )
                  }
                />
              </Stack>
            </Stack>
          </Card>

          {/* SOAP出力設定セクション */}
          <Card
            sx={{
              p: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
              },
              transition: 'box-shadow 0.3s ease',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: colorTokens.blue[700],
                mb: 2,
                pb: 1.5,
                borderBottom: `2px solid ${colorTokens.blue[50]}`,
              }}
            >
              {settingDescriptions.soapOutput.title}
            </Typography>

            <Stack spacing={3}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {
                      settingDescriptions.soapOutput.items.is_enrich_subjective
                        .title
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      settingDescriptions.soapOutput.items.is_enrich_subjective
                        .description
                    }
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={customSetting.is_enrich_subjective || false}
                  onChange={() =>
                    updateSetting(
                      'is_enrich_subjective',
                      !customSetting.is_enrich_subjective
                    )
                  }
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {
                      settingDescriptions.soapOutput.items.should_enhance_soap
                        .title
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      settingDescriptions.soapOutput.items.should_enhance_soap
                        .description
                    }
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={customSetting.should_enhance_soap || false}
                  onChange={() =>
                    updateSetting(
                      'should_enhance_soap',
                      !customSetting.should_enhance_soap
                    )
                  }
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {settingDescriptions.soapOutput.items.is_short_ep.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      settingDescriptions.soapOutput.items.is_short_ep
                        .description
                    }
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={customSetting.is_short_ep ?? false}
                  onChange={() =>
                    updateSetting('is_short_ep', !customSetting.is_short_ep)
                  }
                />
              </Stack>
            </Stack>
          </Card>

          {/* 詳細設定への遷移ボタン */}
          <Box>
            <CustomButton
              variant="outlined"
              fullWidth
              onClick={handleNavigateToDetailSettings}
              startIcon={<SettingsIcon />}
              sx={{
                py: 1.5,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              詳細設定
            </CustomButton>
          </Box>
        </Stack>
      </Box>
    </Modal>
  )
}
